/**
 * Los SIETE centros de costo canónicos de Sage. SIN espacios alrededor del guion.
 *
 * 🔴 Esta lista es la CLAVE del mapeo a GL (`COST_CENTER_TO_GL`). Una grafía que no
 * coincida exactamente deja un centro sin GL, y como el campo de GL se retiró del camino
 * de proyecto, ese ítem no se puede codificar NI corregir desde la UI.
 *
 * ⚠ Hasta acá la lista tenía SEIS valores con las grafías viejas (`04 - Material`
 * singular, `05 - Subcontratos` en español, `06 - Other` singular) mientras la tabla
 * `cost_centers` y los datos de prod ya estaban normalizados a estos siete. Eso no era
 * cosmético: `runSeedCostCentersAndCategories` RE-SIEMBRA desde acá en CADA arranque, así
 * que cada deploy reintroducía los viejos en el dropdown. Ya pasó una vez —convivieron
 * `04 - Material` y `04 - Materials`.
 */
export const COST_CENTERS = [
  "01-Labor",
  "02-Fringes",
  "03-Equipment",
  "04-Materials",
  "05-Subcontracts",
  "06-Others",
  "07-Chiveros",
];

/**
 * Los dos que NO se ofrecen en el camino de GASTOS: la mano de obra y los fringes entran
 * por Nómina. Siguen en `COST_CENTERS` porque el catálogo de Sage los tiene y la tabla
 * `cost_centers` los necesita activos — lo que se filtra es el SELECTOR de gastos.
 */
export const COST_CENTERS_SOLO_NOMINA = ["01-Labor", "02-Fringes"];

/** Los cinco que sí se ofrecen al codificar un gasto de proyecto. */
export const COST_CENTERS_GASTOS = COST_CENTERS.filter(
  (c) => !COST_CENTERS_SOLO_NOMINA.includes(c)
);

/**
 * Grafías viejas a desactivar en el seed. Sin esto vuelven a aparecer en el dropdown
 * después de cualquier deploy que las haya insertado antes.
 *
 * ⚠ Es DESACTIVAR, no borrar: `project_tarea_cost_centers` tiene mapeos tarea↔centro
 * apuntando a esos ids, y borrarlos los rompería.
 */
/**
 * 🔴 EL MAPEO CENTRO DE COSTO → CUENTA GL. Fuente única de la derivación.
 *
 * Verificado contra 79 ítems reales de prod; es el mismo con el que se corrigieron 69 GL
 * el 2026-08-13. **Total sobre los cinco centros de GASTOS** — `01-Labor` y `02-Fringes`
 * no están porque no se codifican por acá.
 *
 * ⚠ NO ES INVERTIBLE: `06-Others` y `07-Chiveros` comparten `6190-00`, así que del GL no
 * se puede volver al centro de costo. Da igual para el export (que solo emite el GL), pero
 * cierra la puerta a derivar al revés.
 *
 * ⚠⚠ El GL dejó de ser un dato que alguien ELIGE y pasó a ser uno que el sistema DERIVA.
 * Un error acá ya no lo atrapa nadie mirando la pantalla — a cambio, son cinco filas
 * revisables de un vistazo en vez de errores repartidos por ítems sueltos.
 */
export const COST_CENTER_TO_GL = {
  "03-Equipment": "6130-00",
  "04-Materials": "6110-00",
  "05-Subcontracts": "6120-00",
  "06-Others": "6190-00",
  "07-Chiveros": "6190-00",
};

export const LEGACY_COST_CENTERS = [
  "01 - Labor",
  "02 - Labor",
  "02 - Fringe",
  "02 - Fringes",
  "03 - Equipment",
  "04 - Material",
  "04 - Materials",
  "05 - Subcontract",
  "05 - Subcontracts",
  "05 - Subcontratos",
  "06 - Other",
  "06 - Others",
  "07 - Chiveros",
];

export const EXPENSE_CATEGORIES = [
  "Travel & Transportation",
  "Meals & Entertainment",
  "Software & Subscriptions",
  "Office Supplies",
  "Hardware & Equipment",
  "Training & Education",
  "Marketing & Advertising",
  "Professional Services",
  "Utilities",
  "Other",
];

// ── Single source of truth for status colors — Gastos + Nómina (V34.1) ──
// `color` = strong (dot / text / badge border); `bg` = light badge fill. Change a color HERE and
// every legend, badge, calendar cell, and chip across both modules updates. Labels live in
// STATUS_LABEL (Gastos) and the per-page configs (Nómina) — this file owns colors only.
export const STATUS_STYLE = {
  draft:               { color: "#7c3aed", bg: "#f3e8ff" },  // Borrador / Sin Enviar — purple
  pending:             { color: "#eab308", bg: "#fef9c3" },  // En Revisión — yellow
  submitted:           { color: "#eab308", bg: "#fef9c3" },  // Sometido (pass-through) — yellow
  pending_engineer:    { color: "#eab308", bg: "#fef9c3" },  // Pend. Ingeniero — yellow
  pending_manager:     { color: "#b45309", bg: "#fef3c7" },  // Pend. Gerente — mustard
  pending_clerk:       { color: "#2563eb", bg: "#dbeafe" },  // Rev. Final — blue (off green)
  pending_review:      { color: "#2563eb", bg: "#dbeafe" },  // Pend. de Revisión (Gastos) — blue
  partially_approved:  { color: "#60a5fa", bg: "#eff6ff" },  // Parc. Aprobado — lighter blue
  approved:            { color: "#16a34a", bg: "#dcfce7" },  // Aprobado — green
  returned:            { color: "#ea580c", bg: "#ffedd5" },  // Devuelto (Nómina) — orange
  sent_back:           { color: "#ea580c", bg: "#ffedd5" },  // Devuelto (Gastos) — orange
  partially_returned:  { color: "#ea580c", bg: "#ffedd5" },  // Parc. Devuelto — orange
  rejected:            { color: "#dc2626", bg: "#fee2e2" },  // Rechazado — red
  paid:                { color: "#475569", bg: "#f1f5f9" },  // Pagado — slate (off green-teal)
  no_card:             { color: "#94a3b8", bg: "#f1f5f9" },  // "Sin tarjeta" (Nómina calendar only)
  // T-31 pieza 4 — Anulado. Gris pizarra OSCURO, distinto del `paid` (slate claro) y sin
  // parecido al rojo de `rejected`: anular no es rechazar. Rechazar es una decisión sobre el
  // gasto; anular es sacar del circuito un registro que no debió existir.
  voided:              { color: "#334155", bg: "#e2e8f0" },  // Anulado — slate oscuro
};

// Gastos badge shape ({ bg, text, border }) derived from STATUS_STYLE — strong color drives both
// text and border. Kept so existing consumers (AdminDashboard, ReceiptViewerModal, etc.) don't break.
export const STATUS_COLORS = Object.fromEntries(
  Object.entries(STATUS_STYLE).map(([k, s]) => [k, { bg: s.bg, text: s.color, border: s.color }])
);

export const STATUS_LABEL = {
  draft:               'Sin Enviar',
  voided:              'Anulado',
  pending:             'Pendiente',
  sent_back:           'Devuelto',
  approved:            'Aprobado',
  rejected:            'Rechazado',
  partially_approved:  'Parcialmente Aprobado',
  partially_returned:  'Parcialmente Devuelto',
  pending_review:      'Pendiente de Revisión',
  paid:                'Pagado',
};

export const REJECTION_REASONS = [
  "Recibo faltante o ilegible",
  "Gasto personal — no elegible para reembolso",
  "Envío duplicado",
  "Monto excede el límite de la política",
  "Fuera de la categoría de gastos aprobada",
  "Fecha del recibo fuera del período permitido",
  "Otro",
];

export const PAYMENT_METHODS = {
  credit_card: 'Tarjeta de Crédito',
  petty_cash: 'Petty Cash',
};

export const PR_TAX_RATES = [
  { value: 11.5, label: '11.5% — IVU Estándar' },
  { value: 7,    label: '7% — IVU Reducido (Alimentos)' },
  { value: 4,    label: '4% — B2B (Servicios)' },
  { value: 0,    label: '0% — Exento' },
];

// Unpaid absence reasons (Nómina V25)
// Paid absences keep using cost codes (01102/01104/01107/01108/01109/01110).
export const ABSENCE_REASONS = {
  class:    'Clase',
  absence:  'Ausencia',
  personal: 'Personal',
};

export const SENDBACK_REASONS = [
  "Información del proveedor incompleta",
  "Monto poco claro o faltante",
  "Fecha faltante o incorrecta",
  "Categoría o centro de costo requiere corrección",
  "Descripción insuficiente",
  "Documentación adicional requerida",
  "Otro",
];
